import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { KeyRound, User, Lock, LogIn, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { LOGO_URL } from "@/lib/api";

function formatErr(detail) {
  if (detail == null) return "Er ging iets mis. Probeer opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail && detail.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { loginPassword, loginLicense } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from && location.state.from !== "/login" ? location.state.from : "/";

  const [mode, setMode] = useState("account"); // "account" | "license"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "account") {
        await loginPassword(username.trim(), password);
      } else {
        await loginLicense(key.trim());
      }
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-screen"
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: "radial-gradient(circle at 30% 20%, #1b2a52 0%, #0d1424 60%, #070b16 100%)" }}
    >
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img src={LOGO_URL} alt="Hitmaster" className="h-24 sm:h-28 drop-shadow-[0_4px_18px_rgba(0,0,0,0.6)]" />
          <p className="mt-3 text-white/70 text-sm text-center">
            Log in om een spel te hosten
          </p>
        </div>

        <div className="bg-white/5 border border-white/15 rounded-3xl p-6 backdrop-blur-md shadow-2xl">
          {/* mode switch */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-black/30 rounded-2xl mb-5">
            <button
              type="button"
              data-testid="login-tab-account"
              onClick={() => { setMode("account"); setError(""); }}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                mode === "account" ? "bg-emerald-500 text-white" : "text-white/60 hover:text-white"
              }`}
            >
              <User className="w-4 h-4" /> Account
            </button>
            <button
              type="button"
              data-testid="login-tab-license"
              onClick={() => { setMode("license"); setError(""); }}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                mode === "license" ? "bg-amber-500 text-white" : "text-white/60 hover:text-white"
              }`}
            >
              <KeyRound className="w-4 h-4" /> Licentie
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "account" ? (
              <>
                <div>
                  <label className="block text-white/70 text-xs mb-1.5">Gebruikersnaam</label>
                  <div className="relative">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      data-testid="login-username"
                      autoCapitalize="none"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full pl-10 pr-3 py-3 rounded-xl bg-black/30 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:border-emerald-400"
                      placeholder="admin"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white/70 text-xs mb-1.5">Wachtwoord</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      data-testid="login-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-3 py-3 rounded-xl bg-black/30 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:border-emerald-400"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-white/70 text-xs mb-1.5">Licentiesleutel (10 cijfers)</label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    data-testid="login-license-key"
                    inputMode="numeric"
                    value={key}
                    onChange={(e) => setKey(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    className="w-full pl-10 pr-3 py-3 rounded-xl bg-black/30 border border-white/15 text-white tracking-[0.3em] placeholder-white/30 focus:outline-none focus:border-amber-400"
                    placeholder="0000000000"
                  />
                </div>
              </div>
            )}

            {error && (
              <div data-testid="login-error" className="text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-base transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
              Inloggen
            </button>
          </form>
        </div>

        <p className="mt-5 text-center text-white/40 text-xs flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Beveiligde toegang · Hitmaster
        </p>
      </div>
    </div>
  );
}
